import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import data from './goldmedalistsinfo.json';
import Tooltip from './Tooltip2';
import ImageScroll from './ImagesScroll';

const ResidenceTiers = () => {
  const svgRef = useRef();
  const containerRef = useRef();
  const scrollTriggerRef = useRef(null);
  // let scrolled = true

  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(null);

  const sex = ['女', '男'];

  // Memoize color scale
  const color = useMemo(() => 
    d3.scaleOrdinal()
      .domain(['男', '女'])
      .range(['#71DF96', '#BB96FF']),
    []
  );

  // Memoize processed data
  // const processedData = useMemo(() => {
  //   const HKOverseasData = data.map(d => ({
  //     ...d,
  //     hometownTier: d.hometownTier === '海外' || d.hometownTier === '香港' ? '海外及香港' : d.hometownTier,
  //     currentTier: d.currentTier === '海外' || d.currentTier === '香港' ? '海外及香港' : d.currentTier
  //   }));
  //   return HKOverseasData.filter(d => d.currentTier !== undefined && d.currentTier !== '不详');
  // }, []);

  const tierLabelMapping = {
    '海外': 'Overseas and Hong Kong',
    '香港': 'Overseas and Hong Kong',
    '一线城市': 'Tier-1 Cities',
    '新一线城市': 'New Tier-1 Cities',
    '二线城市': 'Tier-2 Cities',
    '三线城市': 'Tier-3 Cities',
    '四线城市': 'Tier-4 Cities',
    '五线城市': 'Tier-5 Cities',
    '县级市及以下': 'County-level and Below'
  };

  
  // Memoize processed data to use English tier labels
  const processedData = useMemo(() => {
    const HKOverseasData = data.map(d => ({
      ...d,
      hometownTier: tierLabelMapping[d.hometownTier] || d.hometownTier,
      currentTier: tierLabelMapping[d.currentTier] || d.currentTier 
    }));
    return HKOverseasData.filter(d => d.currentTier !== undefined && d.currentTier !== '不详');
  }, [data]);

  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const drawChart = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;

    // Clear existing content
    d3.select(svgRef.current).selectAll('*').remove();

    gsap.registerPlugin(ScrollTrigger);
    
    const isMobile = window.innerWidth <= 768;
    const container = d3.select(containerRef.current);
    const svg = d3.select(svgRef.current);
    
    // Setup dimensions
    const margin = { top: 10, right: isMobile? 25 : -25, bottom: 50, left: isMobile ? 100 : 140 };
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const width = isMobile ? 
      svgRef.current.getBoundingClientRect().width * 0.73 : 
      containerWidth - margin.left - margin.right;
    const height = isMobile ? 650 : containerHeight - margin.top - margin.bottom - 55;

    // Setup SVG
    svg
      .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr('width', '100%')
      .attr('height', '100%');

    const svgGroup = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Setup scales
    const tierLabels = [
      '海外及香港', '一线城市', '新一线城市', '二线城市', '三线城市', '四线城市', '五线城市', '县级市及以下'
    ];

    

    const x = d3.scalePoint()
      .domain(Object.values(tierLabelMapping))
      .range([0, width])
      .padding(0.5);

    const y = d3.scaleBand()
      .domain(processedData.map(d => d.sport))
      .range([0, height])
      .paddingInner(0.5)
      .paddingOuter(0.5);

    

    // // Background highlight for top sports
    // const topSports = y.domain().slice(0, 12);
    // const topHeight = y.bandwidth() * topSports.length + 6;

    // const backgroundRect = svgGroup.append('rect')
    //   .attr('class', 'row-highlight')
    //   .attr('x', -margin.left + 10)
    //   .attr('y', y(topSports[0]))
    //   .attr('width', width + margin.left + 10)
    //   .attr('height', topHeight)
    //   .attr('fill', 'white')
    //   .attr('opacity', 0)
    //   .attr('z-index', -100);

    ///new
    // Background highlight for top sports
    const topSports = y.domain().slice(0, 11);
    const topHeight = y.bandwidth() * topSports.length + 6;

    const backgroundRect = svgGroup.append('rect')
      .attr('class', 'row-highlight')
      .attr('x', -margin.left + 10)
      .attr('y', y(topSports[0]))
      .attr('width', width + margin.left + 10)
      .attr('height', topHeight)
      .attr('fill', 'white')
      .attr('opacity', 0)
      .attr('z-index', -10);

    const gridGroup = svgGroup.append('g')
      .attr('class', 'grid-layer');

    // Add axes
    const yAxis = d3.axisLeft(y)
      .tickSizeInner(-width)
      .tickSizeOuter(0)
      .tickPadding(5);

    gridGroup.append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .call(g => {
        g.selectAll('.tick line')
          .attr('class', 'grid-lines')
          .attr('x1', 2);
        g.select('.domain').remove();
      });

    const xAxis = d3.axisBottom(x)
      .tickSizeInner(-height)
      .tickSizeOuter(0)
      .tickPadding(10);

    gridGroup.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .call(g => {
        g.selectAll('.tick line')
          .attr('class', 'grid-lines')
          .attr('transform', `translate(${x.step() / 2},0)`);
      });

    // Calculate jitter
    const jitterAmountx = isMobile ? 40 : 85;
    const jitterAmounty = isMobile ? 18 : 8;

    // Prepare data with initial and final positions
    processedData.forEach(d => {
      d.initialX = x(d.hometownTier) + (Math.random() - 0.5) * jitterAmountx;
      d.initialY = y(d.sport) + (Math.random() - 0.5) * jitterAmounty + 7;
      d.finalX = x(d.currentTier) + (Math.random() - 0.5) * jitterAmountx;
      d.finalY = y(d.sport) + (Math.random() - 0.5) * jitterAmounty + 7;
    });

    // Add data points
    const circles = svgGroup.selectAll('.hometown-dot')
      .data(processedData)
      .join('circle')
      .attr('class', 'hometown-dot')
      .attr('cx', d => d.initialX)
      .attr('cy', d => d.initialY)
      .attr('r', 5)
      .style('fill', d => color(d.sex))
      .style('stroke', d => color(d.sex))
      .attr('fill-opacity', 0.4)
      .on('mouseover', (event, d) => {
        const containerRect = containerRef.current.getBoundingClientRect();
        setTooltipContent(`
          <div class="tooltip__content">
            <span class="tooltip__title">
              <b>${d.name}</b> <br/>
              <span style="color:${color(d.sex)}"><b>${d.sex == 'F' ? `Women's` : `Men's`} ${d.sport}</b></span></br>
            </span>
            <span class="tooltip__details">
              Hometown: ${d.hometown}</br>
              Current residence: ${d.currentResidence}
            </span>
          </div>
        `);
        // setTooltipPosition({ x: scrolled ? d.initialX + 110 : d.finalX + 110, y: scrolled ? d.initialY + 60 : d.finalY + 60 });
        setTooltipPosition({ 
          x: event.clientX + window.scrollX,
          y: containerRect.top + (event.clientY - containerRect.top)
        });
        
        setHovered(event.currentTarget);
      })
      .on('mouseout', () => {
        setTooltipContent(null);
        setHovered(null);
      });

    // Create ScrollTrigger
    if (scrollTriggerRef.current) {
      scrollTriggerRef.current.kill();
    }

    scrollTriggerRef.current = ScrollTrigger.create({
      id: 'residence-tiers-id',
      trigger: '#residence-tiers',
      start: '=4% top',
      end: 'bottom bottom',
      scrub: true,
      // markers:true,
      onEnter: self => {
        // scrolled = false
        const progress = self.progress;
        const titleContent = progress > 0.9 
          ? '<span>City tier of <span style="background-color:white; border-bottom:1px solid black;">current residences</span></span>' 
          : '<span style="transform:scale(0.7);">City tier of Olympic champions\' <span style="background-color:white; border-bottom:1px solid black;">hometown</span></span>';

        const titleElement = document.querySelector('.chart-title span');
        if (titleElement) {
          titleElement.innerHTML = titleContent;
        }

        backgroundRect.attr('opacity', progress > 0.5 ? '0.5' : '0');

        circles.each(function(d, i, nodes) {
          d3.select(nodes[i])
            .transition()
            .duration(1500)
            .delay(100)
            // .attr('cx', d => d.initialX + progress * (d.finalX - d.initialX))
            // .attr('cy', d => d.initialY + progress * (d.finalY - d.initialY));
            .attr('cx', d => d.initialX)
            .attr('cy', d => d.initialY);
        });
      },
      onLeave: self => {
        backgroundRect.attr('opacity', '0.5');
        let lastPosition = { x: 0, y: 0 };
        circles.each(function(d, i, nodes) {
          d3.select(nodes[i])
            .transition()
            .duration(1500)
            .attr('cx', d => d.finalX)
            .attr('cy', d => d.finalY);

        });
      },
      onLeaveBack: self => {
        // scrolled = true
        const progress = self.progress;
        const titleContent = progress > 0.9 
          ? '<span>City tier of <span style="background-color:white; border-bottom:1px solid black;">current residences</span></span>' 
          : '<span>City tier of <span style="background-color:white; border-bottom:1px solid black;">hometowns</span></span>';

        const titleElement = document.querySelector('.chart-title span');
        if (titleElement) {
          titleElement.innerHTML = titleContent;
        }

        backgroundRect.attr('opacity', progress > 0.9 ? '0.5' : '0');

        circles.each(function(d, i, nodes) {
          d3.select(nodes[i])
            .transition()
            .duration(1500)
            // .attr('cx', d => d.initialX + progress * (d.finalX - d.initialX))
            // .attr('cy', d => d.initialY + progress * (d.finalY - d.initialY));
            .attr('cx', d => d.initialX)
            .attr('cy', d => d.initialY);

        });
      }
    });

  }, [color, processedData]);

  useEffect(() => {
    const handleResize = debounce(() => {
      drawChart();
    }, 250);

    drawChart();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [drawChart, debounce]);

  return (
    <div>
      
      <div className='content'>
      
        <div className='content__heading'>
          <h2>II. After retirement, Olympic champions are more likely to settle in Tier-1 and Tier-2 cities or move overseas</h2>
          </div>

          <div className='content__text'>
          <p>Against the backdrop of China's rapid urbanization, most Chinese Olympic champions experience an upward mobility in household registration <i>(hukou)</i> status after winning gold medals. The chart below compares their birthplaces and current residences, revealing a significant leftward shift—most were born in third-, fourth-, or fifth-tier cities. A Beijing sports official once stated in an <a href='https://cn.nytimes.com/china/20210730/china-olympics/' target='_blank'>interview</a>: children from rural areas or those from less privileged families tend to be more resilient and hardworking.</p>
          <p>After retirement, the vast majority of Olympic champions settle in second-tier cities or higher.</p>
    
        </div>
      </div>
        
        <div style={{ height: '1500px'}}>
          <div ref= {containerRef} className='chart-container' style={{ position:'sticky', top:'0px' }}>

          {/* <div ref= {containerRef} className='chart-container'> */}

          <div className='chart-title sticky-div-wrapper' style={{ zIndex:10 }}>
            <span>City tier of <span style={{ backgroundColor:'white', borderBottom:'1px solid black' }}>hometowns</span> </span>
          </div>
            
            <div className='content__text' style={{ paddingTop: '10px', paddingBottom:'5px'}}>
              <div className='legend' >
              {sex.map((i) => (
                <div key={i} className='legend__item'>
                  <div className='legend__color round' style={{ backgroundColor:color(i) }}></div>
                  <div className='legend__label'>{i}</div>
                  </div>
              ))}
              </div>
            </div>
            
              <svg ref={svgRef} id='residence-tiers' xmlns="http://www.w3.org/2000/svg" version="1.1"></svg>
              <Tooltip content={tooltipContent} position={tooltipPosition} hovered={hovered} />
          </div>
        </div>
      

      <div className='content'>
      <div className='scroll__caption chart-caption'>Note: Hover over each circle on the chart to view detailed information about each retired champion.</div>
        <div className='content__text'>

          <p>Shooting, badminton, table tennis, volleyball, diving, weightlifting, and gymnastics are China's traditionally dominant Olympic sports. According to WHYNOT’s data analysis, among these seven sports, gymnastics champions have achieved the most significant hukou upward mobility. In contrast, weightlifting athletes face a more challenging transition.</p>
          <p>Over 20 years ago, the article “<a href='https://www.sohu.com/a/217946840_653748' target='_blank'>The Death of a Weightlifting Champion (一个举重冠军之死)</a>” depicted the tragic fate of a former Asian Games weightlifting champion who, after retiring, worked as a security guard and died in poverty due to a lack of medical care. As one media report <a href='https://www.thepaper.cn/newsDetail_forward_13899728' target='_blank'>stated</a>: more than injuries, the greatest challenges these athletes face are the difficulty of monetizing their skills and the low level of professionalization in their sport, which are the main causes of their post-retirement financial struggles.</p>

        </div>

        <ImageScroll
        src1='/images/AP04082104660.jpg'
        caption1={"August 21, 2004, Chinese athlete Tang Gonghong competes in the women's +75kg (over 165 lbs) weightlifting event at the Nikaia Weightlifting Hall during the Athens Summer Olympic Games. Tang set a new world record and won the gold medal. (AP / Charles Krupa)"}
        src2='/images/tanggonghong-douyin.jpg'
        caption2={"August 2023, a picture of Tang Gonghong with her son, published on Douyin. (Douyin / Tang Gonghong)"}
        />


      </div>
    </div>
  );
};

export default ResidenceTiers;